import React from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import H1 from 'apps/Pdf/components/common/H1';
import H2 from 'apps/Pdf/components/common/H2';
import Spacer from 'apps/Pdf/components/common/Spacer';
import PageBreak from 'apps/Pdf/components/common/PageBreak';
import BasicData from 'apps/Pdf/components/BasicData/BasicData';
import PlanBucket from 'apps/Pdf/components/Bucket/PlanBucket';
import Loading from 'apps/Pdf/components/common/Loading';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import { OverviewContent } from './OverviewInterface';
import { Currency } from 'enums';
import {
  PortfolioChange,
  makePortfolioComparison,
  hasTransfers,
  hastTansfersFromCashOnly,
  findPortfolioChanges,
} from 'stores/account/overviewStore/overviewStore';
import PortfolioTableComparison from 'components/PortfolioTables/PortfolioTableComparison';
import OverviewSection from './OverviewSection';
import { Flows } from 'pages/Accounts/Detail/enums';
import GeneralOverview from 'pages/Accounts/Detail/Overview/GeneralOverview';
import { OverviewData } from './OverviewInterface';

const Overview = (params: PdfContentParams) => {
  const styles = useGeneralStyles();
  const { t } = useTranslation();
  const templateType = TPL_DRAFT_OVERVIEW;

  const pdfContent = usePdfContent<OverviewContent>({
    templateType,
    ...params,
  });

  const pdfData = pdfContent?.data ? pdfContent.data : ({} as OverviewContent);
  const data = pdfContent?.data ? pdfData.overview : ({} as OverviewData);
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  const riskChange =
    data?.original?.buckets &&
    data?.proposed?.buckets &&
    data?.proposed?.buckets.some(
      (bucket) =>
        bucket.active &&
        data?.original?.buckets.find((oB) => oB.type === bucket.type)?.riskProfile !== bucket.riskProfile,
    );

  const portfolioChange: PortfolioChange = findPortfolioChanges(data?.original?.buckets, data?.proposed?.buckets);
  const comparison = makePortfolioComparison(data?.original?.buckets, data?.proposed?.buckets);
  const transfers = hasTransfers(data?.flowId, data?.transfers);
  const transfersFromCashOnly = hastTansfersFromCashOnly(data?.transfers);
  const withdrawAll = data?.flowId === Flows.WITHDRAWAL && !data?.withdrawal;

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>Souhrn</Title>

      <BasicData data={pdfData.header} />

      <GeneralOverview
        data={data}
        flow={data.flowId}
        currency={currency}
        transfers={transfers}
        transfersFromCashOnly={transfersFromCashOnly}
        withdrawAll={withdrawAll}
        pdf
      />

      <PageBreak />

      <H1 className="tight">Nové nastavení</H1>

      <OverviewSection data={data.proposed} currency={currency} change={params.change} flow={data.flowId} />

      <PageBreak />

      <H2>{t('accountDetail_overview_bucketsTitle')}</H2>
      <div className={`${styles.threeColumns} ${styles.avoidPageBreak}`}>
        <PlanBucket
          term="longTerm"
          data={data.proposed.buckets[2]}
          originalBucket={data.original.buckets[2]}
          currency={currency}
          riskChange={riskChange}
          rebalancingEnabled={data.rebalancingEnabled}
          hedgingEnabled={data.hedgingEnabled}
        />
        <PlanBucket
          term="midTerm"
          data={data.proposed.buckets[1]}
          originalBucket={data.original.buckets[1]}
          currency={currency}
          riskChange={riskChange}
          rebalancingEnabled={data.rebalancingEnabled}
          hedgingEnabled={data.hedgingEnabled}
        />
        <PlanBucket
          term="shortTerm"
          data={data.proposed.buckets[0]}
          originalBucket={data.original.buckets[0]}
          currency={currency}
          riskChange={riskChange}
          rebalancingEnabled={data.rebalancingEnabled}
          hedgingEnabled={data.hedgingEnabled}
        />
      </div>

      <Spacer />

      <PageBreak />

      {(portfolioChange['LONG_TERM'] || data.proposed.buckets[2].oneTimeRebalance) && (
        <>
          <H2 className="longTerm">
            {t('bucket.composition')} {t(`${data.proposed.buckets[2].title}.detail`)}
          </H2>
          <PortfolioTableComparison
            bucketType={data.proposed.buckets[2].type}
            comparison={comparison[2]}
            automaticRebalancing={!!data.proposed.buckets[2].automaticRebalancing}
            oneTimeRebalance={data.proposed.buckets[2].oneTimeRebalance}
            rebalancingTimeTest={data.proposed.buckets[2].rebalancingTimeTest}
            showRebalancing={true}
            showInstruments={portfolioChange['LONG_TERM']}
          />
          <PageBreak />
        </>
      )}

      {(portfolioChange['MID_TERM'] || data.proposed.buckets[1].oneTimeRebalance) && data.proposed.buckets[1].active && (
        <>
          <H2 className="midTerm">
            {t('bucket.composition')} {t(`${data.proposed.buckets[1].title}.detail`)}
          </H2>
          <PortfolioTableComparison
            bucketType={data.proposed.buckets[1].type}
            comparison={comparison[1]}
            automaticRebalancing={!!data.proposed.buckets[1].automaticRebalancing}
            oneTimeRebalance={data.proposed.buckets[1].oneTimeRebalance}
            rebalancingTimeTest={data.proposed.buckets[1].rebalancingTimeTest}
            showRebalancing={true}
            showInstruments={portfolioChange['MID_TERM']}
          />
          <PageBreak />
        </>
      )}

      {(portfolioChange['SHORT_TERM'] || data.proposed.buckets[0].oneTimeRebalance) && data.proposed.buckets[0].active && (
        <>
          <H2 className="shortTerm">
            {t('bucket.composition')} {t(`${data.proposed.buckets[0].title}.detail`)}
          </H2>
          <PortfolioTableComparison
            bucketType={data.proposed.buckets[0].type}
            comparison={comparison[0]}
            automaticRebalancing={!!data.proposed.buckets[0].automaticRebalancing}
            oneTimeRebalance={data.proposed.buckets[0].oneTimeRebalance}
            rebalancingTimeTest={data.proposed.buckets[0].rebalancingTimeTest}
            showRebalancing={true}
            showInstruments={portfolioChange['SHORT_TERM']}
          />
          <PageBreak />
        </>
      )}

      <H1 className="tight">Původní nastavení</H1>

      <OverviewSection data={data.original} currency={currency} change={params.change} original flow={data.flowId} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_DRAFT_OVERVIEW: TemplateType = 'draftOverview';

export { TPL_DRAFT_OVERVIEW, Overview };
